import React, { useState } from "react";
import { Alert, Box, Button, Skeleton, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import DashboardLayout from "layout";
import ConfirmDialog from "components/ConfirmDialog";
import DataTable, { DataColumn } from "components/DataTable";
import { SummaryRow } from "components/SideDrawer";
import StatusBadge from "components/StatusBadge";
import { useToast } from "components/Toast";
import {
  ESIM_STATUS_TONES,
  EsimOrder,
  getEsimOrder,
  orderCustomerName,
  orderDate,
  orderPackage,
  orderPhone,
  orderTransaction,
  retryEsimOrder,
} from "services/esim";
import { formatNaira, getApiError } from "services/finance";
import { formatDisplayDate } from "utils/helpers";
import { CAN_REQUEST_REFUND, CAN_RESOLVE_REFUND, CAN_RETRY, CopyValue } from "./EsimOrderPanel";
import { RequestRefundDrawer, ResolveRefundDrawer } from "./EsimRefundDrawers";

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box bgcolor="#FFFFFF" border="1px solid #E4E7EC" borderRadius="12px" p="20px">
    <Typography fontSize={16} fontWeight={600} color="#101828" mb="8px">
      {title}
    </Typography>
    {children}
  </Box>
);

type LedgerEntry = Record<string, any>;

const LEDGER_CATEGORY_LABELS: Record<string, string> = {
  esim_sales: "eSIM sale",
  esim_cost: "eSIM cost (Airalo)",
  esim_refund: "Customer refund",
  esim_airalo_credit: "Airalo credit",
};

const humanize = (value?: string) =>
  value ? value.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()) : "—";

const isExpense = (e: LedgerEntry) => e.direction === "expense";

const formatLedgerAmount = (e: LedgerEntry) => {
  const amount = Math.abs(Number(e.amount || 0));
  const value =
    !e.currency || e.currency === "NGN"
      ? formatNaira(amount)
      : `${e.currency} ${amount.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
  return `${isExpense(e) ? "−" : "+"}${value}`;
};

const ledgerColumns: DataColumn<LedgerEntry>[] = [
  {
    label: "Reference",
    render: (e) => <Box sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>{e.reference || "—"}</Box>,
  },
  {
    label: "Type",
    render: (e) => (
      <StatusBadge status={e.direction} tones={{ revenue: "success", expense: "error" }} />
    ),
  },
  {
    label: "Category",
    render: (e) => (
      <>
        {LEDGER_CATEGORY_LABELS[e.category] || humanize(e.category)}
        {e.description && (
          <Typography fontSize={12} color="#667085">
            {e.description}
          </Typography>
        )}
      </>
    ),
  },
  {
    label: "Amount",
    render: (e) => (
      <Box
        sx={{ whiteSpace: "nowrap", fontWeight: 600 }}
        color={isExpense(e) ? "#B42318" : "#027A48"}
      >
        {formatLedgerAmount(e)}
      </Box>
    ),
  },
  { label: "Counts in P&L", render: (e) => (e.affectsPnL === false ? "No" : "Yes") },
  {
    label: "Date",
    render: (e) => (
      <Box sx={{ whiteSpace: "nowrap" }}>{formatDisplayDate(e.date || e.createdAt)}</Box>
    ),
  },
];

// Net of the NGN entries that count towards P&L: sale minus cost (and refunds).
const ledgerNet = (entries: LedgerEntry[]) =>
  entries
    .filter((e) => e.affectsPnL !== false && (!e.currency || e.currency === "NGN"))
    .reduce((sum, e) => sum + (isExpense(e) ? -1 : 1) * Math.abs(Number(e.amount || 0)), 0);

const topupColumns: DataColumn<EsimOrder>[] = [
  { label: "Order ref", render: (t) => t.order_ref },
  { label: "Plan", render: (t) => orderPackage(t).title || orderPackage(t).data || "—" },
  { label: "Total", render: (t) => formatNaira(t.total_price) },
  { label: "Status", render: (t) => <StatusBadge status={t.status} tones={ESIM_STATUS_TONES} /> },
  { label: "Created", render: (t) => formatDisplayDate(orderDate(t)) },
];

const EsimOrderDetail = () => {
  const { orderRef = "" } = useParams();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [confirmRetry, setConfirmRetry] = useState(false);
  const [drawer, setDrawer] = useState<"request" | "resolve" | null>(null);

  const { data: order, isLoading, error } = useQuery({
    queryKey: ["esimOrder", orderRef],
    queryFn: () => getEsimOrder(orderRef),
    enabled: !!orderRef,
  });

  const retry = useMutation({
    mutationFn: () => retryEsimOrder(orderRef),
    onSuccess: (res) => {
      const status = res?.data?.status;
      // A retry can come back still processing; that's progress, not failure.
      showToast(
        res?.message || "Retry started",
        status === "completed" ? "success" : status === "failed" ? "error" : "info",
      );
      setConfirmRetry(false);
      queryClient.invalidateQueries({ queryKey: ["esimOrder", orderRef] });
      queryClient.invalidateQueries({ queryKey: ["esimOrders"] });
    },
    onError: (err) => {
      showToast(getApiError(err, "Retry failed"), "error");
      setConfirmRetry(false);
    },
  });

  const status = order?.status || "";
  const sim = order?.sims?.[0];
  const txn = order ? orderTransaction(order) : undefined;
  // Prefer the virtual-account details; fall back to the recorded transaction.
  const pay = order?.payment_details || (txn && { ...txn, expiresOn: undefined });
  const pkg = order ? orderPackage(order) : undefined;
  const ledger = order?.ledger || order?.ledger_entries || [];
  const topups = order?.topups || [];
  const customerName = order ? orderCustomerName(order) : "";

  return (
    <DashboardLayout nextSideBar subMenu goTo="/esim/orders">
      <Box sx={{ width: "100%", p: { xs: 0, sm: 2 } }}>
        <Box
          mb={3}
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          gap="12px"
        >
          <Box>
            <Box display="flex" alignItems="center" gap="12px">
              <Typography variant="h5" fontWeight={600} color="#101828">
                {orderRef}
              </Typography>
              {order && <StatusBadge status={status} tones={ESIM_STATUS_TONES} />}
            </Box>
            <Typography variant="body2" color="#475467">
              {order?.kind === "topup" ? "Top-up order" : "eSIM order"} · created{" "}
              {order ? formatDisplayDate(orderDate(order)) : "…"}
            </Typography>
          </Box>
          {order && (
            <Box display="flex" gap="8px" flexWrap="wrap">
              {CAN_RETRY.includes(status) && (
                <Button variant="contained" onClick={() => setConfirmRetry(true)} sx={{ textTransform: "none" }}>
                  Retry fulfilment
                </Button>
              )}
              {CAN_REQUEST_REFUND.includes(status) && (
                <Button variant="outlined" onClick={() => setDrawer("request")} sx={{ textTransform: "none" }}>
                  Request refund
                </Button>
              )}
              {CAN_RESOLVE_REFUND.includes(status) && (
                <Button variant="contained" onClick={() => setDrawer("resolve")} sx={{ textTransform: "none" }}>
                  Resolve refund
                </Button>
              )}
            </Box>
          )}
        </Box>

        {error ? (
          <Alert severity="error">{getApiError(error, "Couldn't load this order")}</Alert>
        ) : isLoading || !order ? (
          <Skeleton variant="rounded" height={320} />
        ) : (
          <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap="16px">
            {status === "failed" && (
              <Alert severity="error" sx={{ gridColumn: "1 / -1" }}>
                The customer paid but the eSIM couldn't be issued. Check the Airalo balance, then
                retry, or start a refund.
              </Alert>
            )}

            <Section title="Customer">
              <SummaryRow label="Name" value={customerName || "—"} />
              <SummaryRow label="Email" value={order.email || "—"} />
              <SummaryRow label="Phone" value={orderPhone(order) || "—"} />
            </Section>

            <Section title="Package">
              <SummaryRow label="Destination" value={pkg?.destination_title || "—"} />
              <SummaryRow label="Operator" value={pkg?.operator?.title || "—"} />
              <SummaryRow label="Plan" value={pkg?.title || "—"} />
              <SummaryRow label="Data" value={pkg?.is_unlimited ? "Unlimited" : pkg?.data || "—"} />
              <SummaryRow label="Validity" value={pkg?.day ? `${pkg.day} days` : "—"} />
              <SummaryRow label="Package ID" value={pkg?.package_id || "—"} />
            </Section>

            <Section title="Payment">
              <SummaryRow label="Total" value={formatNaira(order.total_price)} bold />
              {pay ? (
                <>
                  <SummaryRow label="Bank" value={pay.bankName || "—"} />
                  <SummaryRow label="Account" value={[pay.accountNumber, pay.accountName].filter(Boolean).join(" · ") || "—"} />
                  <SummaryRow label="Amount" value={formatNaira(pay.amount)} />
                  {pay.expiresOn && (
                    <SummaryRow label="Account expires" value={formatDisplayDate(pay.expiresOn)} />
                  )}
                  {txn?.transactionDate && (
                    <SummaryRow label="Paid on" value={formatDisplayDate(txn.transactionDate)} />
                  )}
                  {pay.transactionReference && (
                    <SummaryRow label="Reference" value={<CopyValue value={pay.transactionReference} />} />
                  )}
                </>
              ) : (
                <Typography fontSize={13} color="#667085">
                  No payment details on this order.
                </Typography>
              )}
            </Section>

            <Section title="SIM">
              {sim || order.iccid ? (
                <>
                  <SummaryRow label="ICCID" value={<CopyValue value={sim?.iccid || order.iccid} />} />
                  <SummaryRow label="SM-DP+ address" value={<CopyValue value={sim?.lpa} />} />
                  <SummaryRow label="Activation code" value={<CopyValue value={sim?.matching_id} />} />
                  <SummaryRow label="Confirmation code" value={<CopyValue value={sim?.confirmation_code} />} />
                  <SummaryRow
                    label="APN"
                    value={sim?.apn_type === "manual" ? `Manual: ${sim.apn_value || "—"}` : sim?.apn_type || "—"}
                  />
                  {sim?.qrcode_url && (
                    <Box mt="8px">
                      <img src={sim.qrcode_url} alt="eSIM QR code" width={120} height={120} />
                    </Box>
                  )}
                </>
              ) : (
                <Typography fontSize={13} color="#667085">
                  No eSIM issued yet.
                </Typography>
              )}
            </Section>

            <Box sx={{ gridColumn: "1 / -1" }}>
              <Section title={`Top-ups (${topups.length})`}>
                <DataTable
                  columns={topupColumns}
                  rows={topups}
                  emptyText="No top-ups"
                  rowKey={(t) => t.order_ref}
                />
              </Section>
            </Box>

            <Box sx={{ gridColumn: "1 / -1" }}>
              <Section title="Ledger">
                {ledger.length ? (
                  <>
                    <DataTable
                      columns={ledgerColumns}
                      rows={ledger}
                      rowKey={(e, i) => String(e._id ?? e.reference ?? i)}
                    />
                    <Box display="flex" justifyContent="flex-end" gap="8px" mt="12px">
                      <Typography fontSize={14} color="#667085">
                        Margin on this order
                      </Typography>
                      <Typography
                        fontSize={14}
                        fontWeight={700}
                        color={ledgerNet(ledger) < 0 ? "#B42318" : "#101828"}
                      >
                        {formatNaira(ledgerNet(ledger))}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Typography fontSize={13} color="#667085">
                    No ledger entries.
                  </Typography>
                )}
              </Section>
            </Box>
          </Box>
        )}
      </Box>

      <ConfirmDialog
        open={confirmRetry}
        title="Retry fulfilment?"
        message="This asks Airalo to issue the eSIM again. It checks Airalo first, so it won't buy a second eSIM."
        confirmLabel="Retry"
        loading={retry.isPending}
        onConfirm={() => retry.mutate()}
        onClose={() => setConfirmRetry(false)}
      />

      {order && drawer === "request" && (
        <RequestRefundDrawer open onClose={() => setDrawer(null)} order={order} />
      )}
      {order && drawer === "resolve" && (
        <ResolveRefundDrawer open onClose={() => setDrawer(null)} order={order} />
      )}
    </DashboardLayout>
  );
};

export default EsimOrderDetail;
